import { toast } from '../hooks/use-toast';
// Basic shared types for API
export interface Post {
    id: string;
    title: string;
    description: string;
    imageUrl: string;
    creatorName: string;
    creatorIcon: string;
    creatorLevel?: number;
    creatorId?: string;
    likeCount?: number;
    status: string;
    contentType: 'text' | 'image' | 'audio';
    fileUrl: string;
    roleLimits?: { text: number; image: number; audio: number };
    childCounts?: { text: number; image: number; audio: number };
    childPreview?: {
        text: { id: string; description: string; file_url: string; author_name: string } | null;
        image: { id: string; file_url: string; author_name: string } | null;
        audio: { id: string; duration_sec: number; file_url: string; author_name: string } | null;
    };
}

// サイトアセットのキー（workers/anokoe/src/routes/siteAssets.ts の SITE_ASSET_KEYS と1:1）
export type SiteAssetKey = 'logo' | 'heroBg' | 'salonBg' | 'tokiwaBg' | 'stageBg';

// API base URL: empty in dev (same-origin), full URL in production (cross-origin)
export const API_BASE = import.meta.env.VITE_API_BASE || '';

// クロスオリジン(フロント=anokoe.jp / API=app.anokoe.jp)でも Cookie セッションを送るため、
// 全リクエストで credentials:'include' を付与するラッパー。
const afetch = (input: string, init: RequestInit = {}): Promise<Response> =>
    window.fetch(input, { credentials: 'include', ...init });

// コンポーネントが直接 API を叩く場合に使う共通ヘルパー (API_BASE 補完 + credentials)
export const apiFetch = (path: string, init: RequestInit = {}): Promise<Response> =>
    afetch(API_BASE + path, init);

/**
 * 投稿ファイル（画像・音声・テキスト本文）の表示用URLを作る。**必ずこれを通すこと。**
 *
 * 【なぜ要るか】APIは `/file/...` の相対パスを返すが、本番はフロント(anokoe.jp)と
 * API(app.anokoe.jp)が**別オリジン**。相対のまま `<img src>` や fetch に渡すと
 * anokoe.jp 側へ飛び、Pages のSPAフォールバックが index.html を **status 200** で返す。
 * 画像は壊れ、テキスト投稿は本文の代わりにHTMLが表示される（→ CLAUDE.md §4 の1行目）。
 *
 * 【開発環境では壊れない】API_BASE='' ＋ viteプロキシで相対のまま通ってしまうため、
 * ローカルで正常に見えても本番で壊れる。必ず本番相当で確認すること。
 *
 * 受け付ける形（DBの移行期の名残で3形が混在している）:
 *   `/r2/<key>` → `/file/<key>` に寄せる ／ `/file/<key>` ／ 素の `<key>`
 *   `http(s)://…` の外部URLはそのまま返す
 *
 * 署名付きURL（`/file/private/…?exp=…&sig=…`）もそのまま渡してよい。
 * クエリは解析せず、オリジンを前置するだけなので壊れない。
 */
export const resolveMediaUrl = (url?: string | null): string => {
    const raw = (url || '').trim();
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith('/r2/')) return `${API_BASE}/file/${raw.slice(4)}`;
    if (raw.startsWith('/')) return API_BASE + raw;
    return `${API_BASE}/file/${raw}`;
};

// クレジット表示名の解決結果キャッシュ（同一著者のカードが並ぶ一覧での重複取得を防ぐ）。
// null（未存在）もキャッシュして無駄な再取得を避ける。取得失敗はキャッシュしない。
const publicUserCache = new Map<string, { id: string; display_name: string | null; icon_url: string | null } | null>();

// Service to fetch data
export const api = {
    // Auth
    getCurrentUser: async (): Promise<{ success: boolean; data?: { id: string; displayName: string; iconUrl: string; spBalance: number } }> => {
        try {
            const response = await afetch(API_BASE + '/api/auth/me');
            const json = await response.json();
            if (json.success && json.data?.user) {
                const u = json.data.user;
                return {
                    success: true,
                    data: {
                        id: u.id,
                        displayName: u.name || u.display_name,
                        iconUrl: u.icon || u.icon_url,
                        spBalance: u.sp_balance || 0
                    }
                };
            }
            return { success: false };
        } catch (e) {
            return { success: false };
        }
    },

    // Upload
    uploadImage: async (file: File): Promise<{ success: boolean; data?: { url: string; key: string }; error?: string }> => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await afetch(API_BASE + '/api/upload/image', {
                method: 'POST',
                body: formData
            });
            const json = await response.json();
            if (json.success) {
                return { success: true, data: json.data };
            }
            return { success: false, error: json.message || 'アップロードに失敗しました' };
        } catch (e: any) {
            return { success: false, error: e.message || 'アップロードに失敗しました' };
        }
    },

    // ─── サイトアセット（ロゴ・ヒーロー背景・街区背景。Admin登録 → 全画面適用） ───
    getSiteAssets: async (): Promise<Partial<Record<SiteAssetKey, { url: string; updated_at: number }>>> => {
        // 【フェイルセーフ】取得失敗は空マップ = 既定デザインで表示（正常系として扱う）
        try {
            const response = await afetch(API_BASE + '/api/site-assets');
            const json = await response.json();
            return json.success && json.data ? json.data : {};
        } catch (e) {
            console.warn('site-assets: 取得に失敗しました（既定デザインを維持）:', e);
            return {};
        }
    },

    adminListSiteAssets: async (): Promise<{ success: boolean; data?: { key: SiteAssetKey; url: string; file_key: string | null; updated_at: number }[]; error?: string }> => {
        try {
            const response = await afetch(API_BASE + '/api/admin/site-assets');
            const json = await response.json();
            return json.success ? { success: true, data: json.data } : { success: false, error: json.message };
        } catch (e: any) {
            return { success: false, error: e.message || '取得に失敗しました' };
        }
    },

    adminPutSiteAsset: async (key: SiteAssetKey, payload: { url: string; file_key?: string }): Promise<{ success: boolean; error?: string }> => {
        try {
            const response = await afetch(`${API_BASE}/api/admin/site-assets/${key}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const json = await response.json();
            return json.success ? { success: true } : { success: false, error: json.message || '登録に失敗しました' };
        } catch (e: any) {
            return { success: false, error: e.message || '登録に失敗しました' };
        }
    },

    adminDeleteSiteAsset: async (key: SiteAssetKey): Promise<{ success: boolean; error?: string }> => {
        try {
            const response = await afetch(`${API_BASE}/api/admin/site-assets/${key}`, { method: 'DELETE' });
            const json = await response.json();
            return json.success ? { success: true } : { success: false, error: json.message || '解除に失敗しました' };
        } catch (e: any) {
            return { success: false, error: e.message || '解除に失敗しました' };
        }
    },

    getGenesisPosts: async (): Promise<Post[]> => {
        try {
            console.log('API: fetching /api/public/posts?genesis_only=true');
            const response = await afetch(API_BASE + '/api/public/posts?genesis_only=true');
            const json = await response.json();
            console.log('API: response', json);
            if (json.success) {
                return json.data.map((p: any) => ({
                    id: p.id,
                    title: p.title,
                    description: p.description,
                    imageUrl: p.image_url,
                    creatorName: p.author?.name || 'Unknown',
                    creatorIcon: p.author?.icon || '',
                    creatorLevel: p.author?.level || 1,
                    creatorId: p.author?.id,
                    likeCount: p.like_count || 0,
                    status: 'active',
                    contentType: p.content_type,
                    fileUrl: p.file_url,
                    roleLimits: p.role_limits,
                    childCounts: p.child_counts,
                    childPreview: p.child_preview || { text: null, image: null, audio: null },
                }));
            }
            return [];
        } catch (e) {
            // エラーは呼び出し元へ伝播させる（握りつぶすと「空状態」と「障害」を区別できず、
            // ユーザーが障害を「作品がない」と誤読するため。Home側でエラーUI+再試行を表示する）
            console.error('Error fetching genesis posts:', e);
            throw e;
        }
    },

    getPostDetailById: async (id: string): Promise<{ post: Post, parent: any, children: any[] } | null> => {
        try {
            const response = await afetch(`${API_BASE}/api/public/posts/${id}`);
            if (response.status === 404) return null;

            const json = await response.json();
            if (json.success) {
                const p = json.data.post;
                return {
                    post: {
                        id: p.id,
                        title: p.title,
                        description: p.description,
                        imageUrl: p.image_url,
                        creatorName: p.author?.name || 'Unknown',
                        creatorIcon: p.author?.icon || '',
                    creatorLevel: p.author?.level || 1,
                        creatorId: p.author?.id,
                        likeCount: p.like_count || 0,
                        status: 'active',
                        contentType: p.content_type,
                        fileUrl: p.file_url,
                        roleLimits: p.role_limits,
                        childCounts: p.child_counts
                    },
                    parent: json.data.parent,
                    children: json.data.children
                };
            }
            return null;
        } catch (e) {
            console.error('Error fetching post detail:', e);
            return null;
        }
    },

    uploadSubmission: async (
        postId: string, 
        fileBlob: Blob, 
        contentType: 'text' | 'image' | 'audio', 
        fileName: string, 
        duration: number = 0, 
        moodTags: string[] = [], 
        waveformPeaks?: string,
        title?: string,
        description?: string,
        roleLimits?: { text: number; image: number; audio: number },
        visibility?: string,
        // PIPE-01: 街区成果物からの作品化。origin_ref を渡すと entry API が posts に保存し
        // 街区側 published_post_id を書き戻す（省略時は従来どおり通常投稿・互換維持）。
        originRef?: { space: string; item_id: string; version?: number }
    ): Promise<{ success: boolean; id?: string; error?: string }> => {
        const formData = new FormData();
        // Backend currently expects the file to be named 'audio' in formData
        formData.append('audio', fileBlob, fileName);
        formData.append('content_type', contentType);
        formData.append('duration', duration.toString());
        formData.append('mood_tags', JSON.stringify(moodTags));
        if (waveformPeaks) {
            formData.append('waveform_peaks', waveformPeaks);
        }
        if (title) formData.append('title', title);
        if (description) formData.append('description', description);
        if (roleLimits) formData.append('role_limits', JSON.stringify(roleLimits));
        if (visibility) formData.append('visibility', visibility);
        if (originRef) formData.append('origin_ref', JSON.stringify(originRef));

        try {
            const response = await afetch(`${API_BASE}/api/entry/${postId}`, {
                method: 'POST',
                headers: {},
                body: formData
            });

            const data = await response.json();
            if (response.ok && data.success) {
                return { success: true, id: data.data.id };
            } else {
                return { success: false, error: data.message || '投稿の送信に失敗しました' };
            }
        } catch (e: any) {
            console.error('Upload error:', e);
            return { success: false, error: e.message };
        }
    },

    getBattlePairs: async (): Promise<any[]> => {
        try {
            const response = await afetch(API_BASE + '/api/interact/vote/candidates');
            if (!response.ok) {
                console.warn('API call failed');
                throw new Error('Failed to fetch candidates');
            }

            const data = await response.json();

            // API returns an array of pairs, take the first one
            const pair = Array.isArray(data) ? data[0] : data;

            if (!pair || !pair.files || pair.files.length < 2) {
                throw new Error('Invalid data format');
            }

            const left = pair.files[0];
            const right = pair.files[1];

            return [{
                id: pair.id,
                left: {
                    name: left.candidateName,
                    audioSrc: left.url,
                    color: 'text-neon-blue',
                    id: left.submissionId
                },
                right: {
                    name: right.candidateName,
                    audioSrc: right.url,
                    color: 'text-neon-pink',
                    id: right.submissionId
                },
            }];
        } catch (e) {
            console.error('Error fetching battle pairs:', e);
            // [C2] Remove mock fallback - let UI handle the error state properly
            throw e;
        }
    },

    vote: async (_pairId: string, winnerId: string) => {
        try {
            const response = await afetch(`${API_BASE}/api/interact/${winnerId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ type: 'LIKE' })
            });

            if (!response.ok) {
                const err = await response.json();
                console.warn('Vote backend failed:', err);
                return { success: false, error: err.message };
            }
            const json = await response.json();
            const gained = json.data?.adpGained || 0;
            if (gained > 0) {
                toast({ title: `+${gained} ADP 獲得！`, description: '今日のデイリーボーナス' });
            }
            return { success: true, stats: json.data?.stats, adpGained: gained };
        } catch (e) {
            console.error('Vote error:', e);
            return { success: false, error: String(e) };
        }
    },

    // 作品に直接いいねする（Res-Battle投票と同じ /api/interact エンドポイントを利用）。
    // 1ユーザー1回まで。既にいいね済みなら alreadyLiked:true を返す。
    like: async (submissionId: string): Promise<{ success: boolean; alreadyLiked?: boolean; adpGained?: number; error?: string; code?: string }> => {
        try {
            const response = await afetch(`${API_BASE}/api/interact/${submissionId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'LIKE' })
            });
            const json: any = await response.json().catch(() => ({}));
            if (!response.ok) {
                return { success: false, error: json.message, code: json.code };
            }
            const gained = json.data?.adpGained || 0;
            if (gained > 0) {
                toast({ title: `+${gained} ADP 獲得！`, description: '今日のデイリーボーナス' });
            }
            return {
                success: true,
                alreadyLiked: json.data?.status === 'already_liked',
                adpGained: gained
            };
        } catch (e) {
            console.error('Like error:', e);
            return { success: false, error: String(e) };
        }
    },

    // 現在のユーザーがいいね済みのpost idを返す（未ログインは空）
    getLikedIds: async (ids: string[]): Promise<string[]> => {
        if (!ids || ids.length === 0) return [];
        try {
            const res = await afetch(`${API_BASE}/api/public/liked?ids=${encodeURIComponent(ids.join(','))}`);
            const j = await res.json();
            return j && j.success ? (j.data || []) : [];
        } catch { return []; }
    },

    // SP購入パック一覧（configured=false なら決済未設定）
    getSpPacks: async (): Promise<{ packs: Array<{ id: string; sp: number; yen: number; label: string }>; configured: boolean }> => {
        try {
            const res = await afetch(`${API_BASE}/api/billing/sp/packs`);
            const j = await res.json();
            return j && j.success ? j.data : { packs: [], configured: false };
        } catch { return { packs: [], configured: false }; }
    },

    // SP購入のStripe Checkoutを作成し、リダイレクト先URLを返す
    createSpCheckout: async (packId: string): Promise<{ success: boolean; url?: string; code?: string; message?: string }> => {
        try {
            const res = await afetch(`${API_BASE}/api/billing/sp/checkout`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ packId })
            });
            const j = await res.json().catch(() => ({}));
            if (!res.ok) return { success: false, code: j.code, message: j.message };
            return { success: true, url: j.data?.url };
        } catch (e) { return { success: false, message: String(e) }; }
    },

    // ===== フォロー（無料・フェーズA） =====
    getFollowStatus: async (creatorId: string): Promise<{ following: boolean; followerCount: number }> => {
        try {
            const r = await afetch(`${API_BASE}/api/follow/${creatorId}/status`);
            const j = await r.json();
            return j && j.success ? j.data : { following: false, followerCount: 0 };
        } catch { return { following: false, followerCount: 0 }; }
    },
    setFollow: async (creatorId: string, follow: boolean): Promise<{ success: boolean; following?: boolean; followerCount?: number; code?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/follow/${creatorId}`, { method: follow ? 'POST' : 'DELETE' });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) return { success: false, code: j.code };
            return { success: true, following: j.data?.following, followerCount: j.data?.followerCount };
        } catch (e) { return { success: false }; }
    },

    // ===== ポートフォリオ（本人専用） =====
    getMyPortfolio: async (): Promise<{ profile: any; works: any[] } | null> => {
        try {
            const r = await afetch(`${API_BASE}/api/portfolio/me`);
            const j = await r.json();
            return j && j.success ? j.data : null;
        } catch { return null; }
    },
    savePortfolio: async (settings: any): Promise<boolean> => {
        try {
            const r = await afetch(`${API_BASE}/api/portfolio/me`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(settings) });
            return r.ok;
        } catch { return false; }
    },
    savePortfolioItem: async (postId: string, meta: any): Promise<boolean> => {
        try {
            const r = await afetch(`${API_BASE}/api/portfolio/item/${postId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(meta) });
            return r.ok;
        } catch { return false; }
    },

    login: async (token: string): Promise<{ success: boolean; user?: any; error?: string }> => {
        try {
            // Start OAuth Flow
            window.location.href = `${API_BASE}/api/auth/google${token ? `?turnstile=${token}` : ''}`;
            return { success: true };
        } catch (e) {
            console.error('Login error:', e);
            return { success: false, error: String(e) };
        }
    },

    // User Profile API
    getUserProfile: async (): Promise<{ success: boolean; data?: any; error?: string }> => {
        try {
            const response = await afetch(API_BASE + '/api/user/me');
            const json = await response.json();
            if (json.success) return { success: true, data: json.data };
            return { success: false, error: json.message };
        } catch (e) {
            return { success: false, error: String(e) };
        }
    },

    getProficiency: async (): Promise<{ success: boolean; data?: any; error?: string }> => {
        try {
            const response = await afetch(API_BASE + '/api/user/proficiency');
            const json = await response.json();
            if (json.success) return { success: true, data: json.data };
            return { success: false, error: json.message };
        } catch (e) {
            return { success: false, error: String(e) };
        }
    },

    getUserBadges: async (): Promise<{ success: boolean; data?: string[]; error?: string }> => {
        try {
            const response = await afetch(API_BASE + '/api/user/badges');
            const json = await response.json();
            if (json.success) return { success: true, data: json.data || [] };
            return { success: false, data: [], error: json.message };
        } catch (e) {
            return { success: false, data: [], error: String(e) };
        }
    },

    getUserGoals: async (): Promise<{ success: boolean; data?: any[]; error?: string }> => {
        try {
            const response = await afetch(API_BASE + '/api/user/goals');
            const json = await response.json();
            if (json.success) return { success: true, data: json.data || [] };
            return { success: false, data: [], error: json.message };
        } catch (e) {
            return { success: false, data: [], error: String(e) };
        }
    },

    evaluateBadges: async (): Promise<{ success: boolean; data?: any }> => {
        try {
            const response = await afetch(API_BASE + '/api/user/badges/evaluate', { method: 'POST' });
            const json = await response.json();
            return json;
        } catch (e) {
            return { success: false };
        }
    },

    updateProfile: async (data: { displayName?: string, iconUrl?: string, isOnboarded?: boolean }): Promise<{ success: boolean; error?: string }> => {
        try {
            const response = await afetch(API_BASE + '/api/user/me', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });
            const json = await response.json();
            if (json.success) return { success: true };
            return { success: false, error: json.message };
        } catch {
            // 例外の文字列（`TypeError: Failed to fetch` 等）を**そのままUIに出さない**。
            // ここは登録直後のオンボーディングでも使われ、英語の例外文が最初の画面に出ていた（2026-08-11）
            return { success: false, error: '通信できませんでした。つながりを確かめて、もう一度ためしてください。' };
        }
    },

    getNotifications: async (): Promise<{ success: boolean; data?: any[] }> => {
        try {
            const response = await afetch(API_BASE + '/api/user/notifications');
            const json = await response.json();
            return json;
        } catch (e) {
            return { success: false };
        }
    },

    markNotificationsRead: async (): Promise<{ success: boolean }> => {
        try {
            const response = await afetch(API_BASE + '/api/user/notifications/read-all', { method: 'POST' });
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    getUserSubmissions: async (): Promise<any[]> => {
        try {
            const response = await afetch(API_BASE + '/api/user/me/submissions');
            const json = await response.json();
            return json.success ? json.data : [];
        } catch (e) {
            console.error(e);
            return [];
        }
    },

    getUserLikes: async (): Promise<any[]> => {
        try {
            const response = await afetch(API_BASE + '/api/user/me/likes');
            const json = await response.json();
            return json.success ? json.data : [];
        } catch (e) {
            console.error(e);
            return [];
        }
    },

    // Admin API
    getAdminStats: async () => {
        try {
            const response = await afetch(API_BASE + '/api/admin/stats');
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    getAdminQueue: async () => {
        try {
            const response = await afetch(API_BASE + '/api/admin/queue');
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    reviewSubmission: async (id: string, action: 'approve' | 'reject') => {
        try {
            const response = await afetch(`${API_BASE}/api/admin/submissions/${id}/review`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action })
            });
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    adminPunishUser: async (userId: string, action: string, reason: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const response = await afetch(`${API_BASE}/api/admin/users/${userId}/punish`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action, reason_code: reason })
            });
            const json = await response.json();
            if (json.success) return { success: true };
            return { success: false, error: json.error };
        } catch (e) {
            return { success: false, error: String(e) };
        }
    },

    createTheme: async (data: { title: string, description?: string, image_url?: string }): Promise<{ success: boolean; error?: string }> => {
        try {
            const response = await afetch(API_BASE + '/api/admin/themes', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });
            const json = await response.json();
            if (json.success) return { success: true };
            return { success: false, error: json.error };
        } catch (e: any) {
            return { success: false, error: e.message };
        }
    },

    suggestTheme: async (data: { title: string, description?: string, image_url?: string }): Promise<{ success: boolean; error?: string }> => {
        try {
            const response = await afetch(API_BASE + '/api/user/themes', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(data)
            });
            const json = await response.json();
            if (json.success) return { success: true };
            return { success: false, error: json.error };
        } catch (e: any) {
            return { success: false, error: e.message };
        }
    },

    getAdminThemeQueue: async (): Promise<{ success: boolean; data?: any[] }> => {
        try {
            const response = await afetch(API_BASE + '/api/admin/themes/queue');
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    reviewTheme: async (id: string, action: 'approve' | 'reject'): Promise<{ success: boolean }> => {
        try {
            const response = await afetch(`${API_BASE}/api/admin/themes/${id}/review`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action })
            });
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    getAdminThemes: async (): Promise<{ success: boolean; data?: any[] }> => {
        try {
            const response = await afetch(API_BASE + '/api/admin/themes');
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    getAdminUsers: async (): Promise<{ success: boolean; data?: any[] }> => {
        try {
            const response = await afetch(API_BASE + '/api/admin/users');
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    deleteTheme: async (id: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const response = await afetch(`${API_BASE}/api/admin/themes/${id}`, {
                method: 'DELETE'
            });
            const json = await response.json();
            return json;
        } catch (e: any) {
            return { success: false, error: e.message };
        }
    },

    // Timed Comments API
    getTimedComments: async (submissionId: string): Promise<{ success: boolean; data?: any[] }> => {
        try {
            const response = await afetch(`${API_BASE}/api/comments/${submissionId}`);
            return await response.json();
        } catch (e) {
            console.error('Error fetching timed comments:', e);
            return { success: false };
        }
    },

    postTimedComment: async (submissionId: string, timestampSec: number, content: string): Promise<{ success: boolean; data?: any; error?: string }> => {
        try {
            const response = await afetch(`${API_BASE}/api/comments/${submissionId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ timestampSec, content })
            });
            return await response.json();
        } catch (e: any) {
            return { success: false, error: e.message };
        }
    },

    deleteTimedComment: async (commentId: string): Promise<{ success: boolean }> => {
        try {
            const response = await afetch(`${API_BASE}/api/comments/item/${commentId}`, {
                method: 'DELETE'
            });
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    // Gift API
    getGiftSummary: async (submissionId: string): Promise<{ success: boolean; data?: any }> => {
        try {
            const response = await afetch(`${API_BASE}/api/gifts/${submissionId}`);
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    sendGift: async (submissionId: string, giftType: string, amount: number = 1): Promise<{ success: boolean; data?: any; message?: string }> => {
        try {
            const response = await afetch(`${API_BASE}/api/gifts/${submissionId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ giftType, amount })
            });
            return await response.json();
        } catch (e: any) {
            return { success: false, message: e.message };
        }
    },

    getGiftTypes: async (): Promise<{ success: boolean; data?: any[] }> => {
        try {
            const response = await afetch(API_BASE + '/api/gifts/types/list');
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    // Repost API
    getRepostStatus: async (submissionId: string): Promise<{ success: boolean; data?: any }> => {
        try {
            const response = await afetch(`${API_BASE}/api/reposts/${submissionId}`);
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    createRepost: async (submissionId: string): Promise<{ success: boolean; data?: any }> => {
        try {
            const response = await afetch(`${API_BASE}/api/reposts/${submissionId}`, { method: 'POST' });
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    deleteRepost: async (submissionId: string): Promise<{ success: boolean; data?: any }> => {
        try {
            const response = await afetch(`${API_BASE}/api/reposts/${submissionId}`, { method: 'DELETE' });
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    // ──── Creator Insights (Phase 4) ────

    getInsightsTips: async (): Promise<{ success: boolean; data?: any[]; summary?: any }> => {
        try {
            const response = await afetch(API_BASE + '/api/user/insights/tips');
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    getInsightsSupporters: async (): Promise<{ success: boolean; data?: any[]; summary?: any }> => {
        try {
            const response = await afetch(API_BASE + '/api/user/insights/supporters');
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    getInsightsHeatmap: async (): Promise<{ success: boolean; heatmap?: any[]; peak?: any }> => {
        try {
            const response = await afetch(API_BASE + '/api/user/insights/heatmap');
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    getInsightsTopSupporters: async (): Promise<{ success: boolean; data?: any[] }> => {
        try {
            const response = await afetch(API_BASE + '/api/user/insights/top-supporters');
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    getCreatorGoals: async (): Promise<{ success: boolean; data?: any[] }> => {
        try {
            const response = await afetch(API_BASE + '/api/user/insights/goals');
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    createCreatorGoal: async (targetAmount: number, displayText: string, deadline?: string): Promise<{ success: boolean }> => {
        try {
            const response = await afetch(API_BASE + '/api/user/insights/goals', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ targetAmount, displayText, deadline }),
            });
            return await response.json();
        } catch (e) {
            return { success: false };
        }
    },

    // ===== プリセット・マーケット（無料先行 / 有料はフラグ待機）=====
    listMarketAssets: async (params: { kind?: string; q?: string; sort?: string; free?: boolean; limit?: number } = {}): Promise<{ paidEnabled: boolean; assets: MarketAsset[] }> => {
        try {
            const qs = new URLSearchParams();
            if (params.kind) qs.set('kind', params.kind);
            if (params.q) qs.set('q', params.q);
            if (params.sort) qs.set('sort', params.sort);
            if (params.free) qs.set('free', '1');
            if (params.limit) qs.set('limit', String(params.limit));
            const res = await afetch(`${API_BASE}/api/market/assets?${qs.toString()}`);
            const json = await res.json();
            if (json.success) return { paidEnabled: !!json.data.paidEnabled, assets: json.data.assets };
            return { paidEnabled: false, assets: [] };
        } catch (e) {
            return { paidEnabled: false, assets: [] };
        }
    },

    getMarketAsset: async (id: string): Promise<{ paidEnabled: boolean; owned: boolean; asset: MarketAsset } | null> => {
        try {
            const res = await afetch(`${API_BASE}/api/market/assets/${id}`);
            const json = await res.json();
            return json.success ? json.data : null;
        } catch (e) {
            return null;
        }
    },

    publishMarketAsset: async (payload: { kind: string; title: string; description?: string; coverUrl?: string; assetUrl: string; license?: string; priceAdp?: number; originAssetId?: string }): Promise<{ success: boolean; data?: { id: string; status: string }; code?: string; message?: string }> => {
        try {
            const res = await afetch(`${API_BASE}/api/market/assets`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            return await res.json();
        } catch (e: any) {
            return { success: false, message: e?.message };
        }
    },

    acquireMarketAsset: async (id: string): Promise<{ success: boolean; data?: { alreadyOwned: boolean; paid?: boolean; priceAdp?: number; newBalance?: number }; code?: string; message?: string }> => {
        try {
            const res = await afetch(`${API_BASE}/api/market/assets/${id}/acquire`, { method: 'POST' });
            return await res.json();
        } catch (e: any) {
            return { success: false, message: e?.message };
        }
    },

    getMarketLibrary: async (): Promise<MarketAsset[]> => {
        try {
            const res = await afetch(`${API_BASE}/api/market/me/library`);
            const json = await res.json();
            return json.success ? json.data.assets : [];
        } catch (e) {
            return [];
        }
    },

    getMarketShop: async (): Promise<{ paidEnabled: boolean; revenueAdp: number; soldCount: number; listings: MarketListing[] } | null> => {
        try {
            const res = await afetch(`${API_BASE}/api/market/me/shop`);
            const json = await res.json();
            return json.success ? json.data : null;
        } catch (e) {
            return null;
        }
    },

    // 管理: 審査キュー & 承認/却下（IS_ADMIN 権限が必要）
    getMarketQueue: async (): Promise<MarketAsset[]> => {
        try {
            const res = await afetch(`${API_BASE}/api/market/admin/queue`);
            const json = await res.json();
            return json.success ? json.data.assets : [];
        } catch (e) {
            return [];
        }
    },

    reviewMarketAsset: async (id: string, action: 'approve' | 'reject'): Promise<{ success: boolean; code?: string; message?: string }> => {
        try {
            const res = await afetch(`${API_BASE}/api/market/admin/${id}/review`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action }),
            });
            return await res.json();
        } catch (e: any) {
            return { success: false, message: e?.message };
        }
    },

    // ===================================================================
    // 三役の街（Phase 1「開街」）— salon / tokiwa / stage
    // 読み取りは全てフェイルセーフ（失敗時は空配列/null。エラーUIは呼び出し元の状態で判定）。
    // 書き込みは { success, ... } を返し、呼び出し元でトースト/再試行を出す。
    // ===================================================================

    // ── 文壇サロン ──
    salonListNotes: async (cursor?: number): Promise<SalonNote[]> => {
        try {
            const qs = cursor ? `?cursor=${cursor}` : '';
            const r = await afetch(`${API_BASE}/api/salon/notes${qs}`);
            const j = await r.json();
            return j && j.success ? (j.data || []) : [];
        } catch { return []; }
    },
    salonGetNote: async (id: string): Promise<SalonNote | null> => {
        try {
            const r = await afetch(`${API_BASE}/api/salon/notes/${id}`);
            if (r.status === 404) return null;
            const j = await r.json();
            return j && j.success ? j.data : null;
        } catch { return null; }
    },
    salonCreateNote: async (payload: { title?: string; body: string; draft_text?: string; linked_post_id?: string | null; status?: 'draft' | 'public' }): Promise<{ success: boolean; id?: string; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/salon/notes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true, id: j.data?.id } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    salonUpdateNote: async (id: string, payload: Partial<{ title: string; body: string; draft_text: string; status: 'draft' | 'public' }>): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/salon/notes/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    salonDeleteNote: async (id: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/salon/notes/${id}`, { method: 'DELETE' });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    salonListShelves: async (userId: string): Promise<SalonShelfItem[]> => {
        try {
            const r = await afetch(`${API_BASE}/api/salon/shelves/${userId}`);
            const j = await r.json();
            return j && j.success ? (j.data || []) : [];
        } catch { return []; }
    },
    salonAddShelf: async (payload: { title: string; author_name?: string; note?: string; category?: string; spine_design?: string }): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/salon/shelves`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    salonDeleteShelf: async (id: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/salon/shelves/${id}`, { method: 'DELETE' });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    salonListAnnotations: async (noteId: string): Promise<SalonAnnotation[]> => {
        try {
            const r = await afetch(`${API_BASE}/api/salon/notes/${noteId}/annotations`);
            const j = await r.json();
            return j && j.success ? (j.data || []) : [];
        } catch { return []; }
    },
    salonAddAnnotation: async (noteId: string, payload: { body: string; range_start?: number; range_end?: number; parent_id?: string }): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/salon/notes/${noteId}/annotations`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    salonDeleteAnnotation: async (id: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/salon/annotations/${id}`, { method: 'DELETE' });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },

    // サロン「今週の一筆」お題（Admin手動運用・決定19）。null = 「次のお題を準備中」（決定3）
    salonCurrentPrompt: async (): Promise<TownPrompt | null> => {
        try {
            const r = await afetch(`${API_BASE}/api/salon/prompts/current`);
            const j = await r.json();
            return j && j.success ? j.data : null;
        } catch { return null; }
    },

    // ── Admin: 週替わりお題管理（tokiwa / salon 共通・決定19） ──
    adminListPrompts: async (space: TownPromptSpace): Promise<TownPrompt[]> => {
        try {
            const r = await afetch(`${API_BASE}/api/admin/prompts?space=${space}`);
            const j = await r.json();
            return j && j.success ? (j.data || []) : [];
        } catch { return []; }
    },
    adminCreatePrompt: async (payload: { space: TownPromptSpace; title: string; description?: string; active_from: number; active_to: number }): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/admin/prompts`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
            });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.error || j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    adminDeletePrompt: async (space: TownPromptSpace, id: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/admin/prompts/${space}/${id}`, { method: 'DELETE' });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.error || j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },

    // ── Admin: 運用イベント（「静かな失敗」の可視化・migration 0029） ──
    // 失敗時は空で返す。**件数を偽らないため、取得できなかったことは呼び出し側で区別する**
    adminListOpsEvents: async (kind?: string, limit = 100): Promise<OpsEvent[] | null> => {
        try {
            const q = new URLSearchParams({ limit: String(limit) });
            if (kind) q.set('kind', kind);
            const r = await afetch(`${API_BASE}/api/admin/ops-events?${q}`);
            const j = await r.json();
            return j && j.success ? (j.data || []) : null;
        } catch { return null; }
    },
    adminOpsEventsSummary: async (): Promise<OpsEventSummary | null> => {
        try {
            const r = await afetch(`${API_BASE}/api/admin/ops-events/summary`);
            const j = await r.json();
            return j && j.success ? j.data : null;
        } catch { return null; }
    },

    // ── トキワ荘 ──
    // TKW-03 廊下ギャラリー（opt-in・決定20）。並びはセレンディピティ（サーバ側RANDOM）、人気順は無い
    tokiwaGallery: async (): Promise<TokiwaSketch[]> => {
        try {
            const r = await afetch(`${API_BASE}/api/tokiwa/gallery`);
            const j = await r.json();
            return j && j.success ? (j.data || []) : [];
        } catch { return []; }
    },
    tokiwaToggleGallery: async (sketchId: string, inGallery: boolean): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/tokiwa/sketches/${sketchId}/gallery`, {
                method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ in_gallery: inGallery }),
            });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },

    tokiwaCurrentPrompt: async (): Promise<TokiwaPrompt | null> => {
        try {
            const r = await afetch(`${API_BASE}/api/tokiwa/prompts/current`);
            const j = await r.json();
            return j && j.success ? j.data : null; // null = 「次のお題を準備中」（決定3）
        } catch { return null; }
    },
    tokiwaListSketchesMine: async (): Promise<TokiwaSketch[]> => {
        try {
            const r = await afetch(`${API_BASE}/api/tokiwa/sketches/mine`);
            const j = await r.json();
            return j && j.success ? (j.data || []) : [];
        } catch { return []; }
    },
    tokiwaSaveSketch: async (payload: { mode: 'dessin' | 'prompt'; image_url: string; image_key?: string; prompt_id?: string; elapsed_sec?: number }): Promise<{ success: boolean; id?: string; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/tokiwa/sketches`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true, id: j.data?.id } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    tokiwaDeleteSketch: async (id: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/tokiwa/sketches/${id}`, { method: 'DELETE' });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },

    // ── 声劇ステージ ──
    stageListScripts: async (cursor?: number): Promise<StageScript[]> => {
        try {
            const qs = cursor ? `?cursor=${cursor}` : '';
            const r = await afetch(`${API_BASE}/api/stage/scripts${qs}`);
            const j = await r.json();
            return j && j.success ? (j.data || []) : [];
        } catch { return []; }
    },
    stageGetScript: async (id: string): Promise<(StageScript & { castings?: StageCasting[] }) | null> => {
        try {
            const r = await afetch(`${API_BASE}/api/stage/scripts/${id}`);
            if (r.status === 404) return null;
            const j = await r.json();
            return j && j.success ? j.data : null;
        } catch { return null; }
    },
    stageCreateScript: async (payload: { title: string; body: string; role_slots?: StageSlot[]; origin_ref?: { space: string; item_id: string; version?: number }; credited_author?: string }): Promise<{ success: boolean; id?: string; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/stage/scripts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true, id: j.data?.id } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    stageListCastings: async (): Promise<StageCasting[]> => {
        try {
            const r = await afetch(`${API_BASE}/api/stage/castings`);
            const j = await r.json();
            return j && j.success ? (j.data || []) : [];
        } catch { return []; }
    },
    stageGetCasting: async (id: string): Promise<(StageCasting & { slots: StageSlot[]; applications: StageApplication[]; progress?: CastingProgress }) | null> => {
        try {
            const r = await afetch(`${API_BASE}/api/stage/castings/${id}`);
            if (r.status === 404) return null;
            const j = await r.json();
            return j && j.success ? j.data : null;
        } catch { return null; }
    },
    stageCreateCasting: async (payload: { script_id: string; title?: string; slots: StageSlot[] }): Promise<{ success: boolean; id?: string; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/stage/castings`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true, id: j.data?.id } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    stageApply: async (castingId: string, roleId: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/stage/castings/${castingId}/apply`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ role_id: roleId }) });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    stageAccept: async (castingId: string, applicationId: string): Promise<{ success: boolean; status?: string; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/stage/castings/${castingId}/accept`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ application_id: applicationId }) });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true, status: j.data?.status } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    // ===================================================================
    // リアルタイム（RT-05 共有キャンバス）— Phase 3-A
    // disabled:true が返る間は「準備中」。数値は実データのみ（participant_count は null=確認中）
    // ===================================================================
    rtListRooms: async (): Promise<{ rooms: CanvasRoom[]; disabled: boolean }> => {
        try {
            const r = await afetch(`${API_BASE}/api/rt/rooms`);
            const j = await r.json();
            if (!j || !j.success) return { rooms: [], disabled: false };
            return { rooms: j.data || [], disabled: !!j.disabled };
        } catch { return { rooms: [], disabled: false }; }
    },
    rtGetRoom: async (id: string): Promise<{ room: CanvasRoom | null; disabled: boolean }> => {
        try {
            const r = await afetch(`${API_BASE}/api/rt/rooms/${id}`);
            if (r.status === 404) return { room: null, disabled: false };
            const j = await r.json();
            return { room: j?.data ?? null, disabled: !!j?.disabled };
        } catch { return { room: null, disabled: false }; }
    },
    rtCreateRoom: async (title: string): Promise<{ success: boolean; id?: string; code?: string; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/rt/rooms`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ title }),
            });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true, id: j.data?.id } : { success: false, code: j.code, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    /**
     * WS接続チケットを取る。**単回使用**なので再接続のたびに必ず取り直すこと
     * （使い回すと初回だけ繋がり、以降すべて401になる）。
     */
    rtGetTicket: async (roomId: string): Promise<{ success: boolean; ticket?: string; code?: string; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/rt/rooms/${roomId}/ticket`, { method: 'POST' });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true, ticket: j.data?.ticket } : { success: false, code: j.code, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    rtCloseRoom: async (roomId: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/rt/rooms/${roomId}/close`, { method: 'POST' });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },

    // ── 稽古場（RT-01 / Phase 3-B・B-1）──
    // disabled は「準備中」を正直に出すためのフラグ（フラグOFF時にサーバーが返す）
    stageListRooms: async (): Promise<{ rooms: StageRoom[]; disabled: boolean }> => {
        try {
            const r = await afetch(`${API_BASE}/api/rt/stage/rooms`);
            const j = await r.json();
            return { rooms: j?.success ? (j.data || []) : [], disabled: j?.disabled === true };
        } catch { return { rooms: [], disabled: false }; }
    },
    stageGetRoom: async (id: string): Promise<{ room: StageRoom | null; disabled: boolean }> => {
        try {
            const r = await afetch(`${API_BASE}/api/rt/stage/rooms/${id}`);
            const j = await r.json();
            return { room: j?.success ? (j.data ?? null) : null, disabled: j?.disabled === true };
        } catch { return { room: null, disabled: false }; }
    },
    /** performerCapacity は 1〜4（未指定は既定4）。範囲外はサーバーが422で弾く */
    stageCreateRoom: async (title: string, castingId?: string, performerCapacity?: number): Promise<{ success: boolean; id?: string; code?: string; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/rt/stage/rooms`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    title,
                    ...(castingId ? { casting_id: castingId } : {}),
                    ...(performerCapacity !== undefined ? { performer_capacity: performerCapacity } : {}),
                }),
            });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true, id: j.data?.id } : { success: false, code: j.code, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    /**
     * **チケットは単回使用**。再接続のたびに必ず取り直す（使い回すと初回だけ繋がる）。
     * `role` は**希望**で、定員を見て確定するのはサーバー（満員なら見学者に振り替わる）。
     */
    stageGetTicket: async (roomId: string, role: 'performer' | 'listener' = 'performer'): Promise<{ success: boolean; ticket?: string; code?: string; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/rt/stage/rooms/${roomId}/ticket`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role }),
            });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true, ticket: j.data?.ticket } : { success: false, code: j.code, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    stageCloseRoom: async (roomId: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/rt/stage/rooms/${roomId}/close`, { method: 'POST' });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },

    // ===================================================================
    // 掲示板（BBS）— 擬似匿名（handle固定）。公開GETに author_id は含まれない（BBS_SPEC §6）
    // ===================================================================
    boardListThreads: async (space: BoardSpace): Promise<BoardThread[]> => {
        try {
            const r = await afetch(`${API_BASE}/api/board/threads?space=${space}`);
            const j = await r.json();
            return j && j.success ? (j.data || []) : [];
        } catch { return []; }
    },
    boardGetThread: async (id: string): Promise<{ thread: BoardThread; replies: BoardReply[] } | null> => {
        try {
            const r = await afetch(`${API_BASE}/api/board/threads/${id}`);
            if (r.status === 404) return null;
            const j = await r.json();
            return j && j.success ? j.data : null;
        } catch { return null; }
    },
    boardCreateThread: async (payload: { space: BoardSpace; title: string; body: string; attach_ref?: BoardAttach | null }): Promise<{ success: boolean; id?: string; code?: string; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/board/threads`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
            });
            const j = await r.json().catch(() => ({}));
            // 409 HANDLE_REQUIRED はフロントでハンドル設定モーダルを開く合図（BBS-05）
            return r.ok && j.success ? { success: true, id: j.data?.id } : { success: false, code: j.code, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    boardCreateReply: async (threadId: string, payload: { body: string; attach_ref?: BoardAttach | null }): Promise<{ success: boolean; code?: string; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/board/threads/${threadId}/replies`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
            });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, code: j.code, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    boardDeleteThread: async (id: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/board/threads/${id}`, { method: 'DELETE' });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    boardDeleteReply: async (id: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/board/replies/${id}`, { method: 'DELETE' });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    boardToggleReaction: async (targetType: 'thread' | 'reply', targetId: string): Promise<{ success: boolean; reacted?: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/board/reactions`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ target_type: targetType, target_id: targetId }),
            });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true, reacted: j.data?.reacted } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    boardGetHandle: async (): Promise<string | null> => {
        try {
            const r = await afetch(`${API_BASE}/api/board/handle`);
            const j = await r.json();
            return j && j.success ? j.data : null;
        } catch { return null; }
    },
    boardSetHandle: async (handle: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/board/handle`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ handle }),
            });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },

    // PIPE-02: サロンの公開台本（draft_text 保持ノート）をステージへ参照インポート。
    // 原作者は credited_author に保存され自動クレジットされる（本文は参照時点のスナップショット・決定11）。
    importScriptFromSalon: async (noteId: string): Promise<{ success: boolean; id?: string; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/stage/scripts/import-from-salon`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note_id: noteId }),
            });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true, id: j.data?.id } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },

    // クレジット表示名の解決（display_name / icon_url のみ）。
    // 【フェイルセーフ】未存在・障害時は null を返す。呼び出し元は名前を捏造せず既定表示にフォールバックする。
    getPublicUser: async (id: string): Promise<{ id: string; display_name: string | null; icon_url: string | null } | null> => {
        if (!id) return null;
        if (publicUserCache.has(id)) return publicUserCache.get(id)!;
        try {
            const r = await afetch(`${API_BASE}/api/public/users/${encodeURIComponent(id)}`);
            const j = await r.json();
            const data = j && j.success ? j.data : null;
            publicUserCache.set(id, data);
            return data;
        } catch {
            return null; // 失敗はキャッシュしない（次回再試行できるように）
        }
    },

    stageSubmitRecording: async (castingId: string, applicationId: string, submissionPostId: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/stage/castings/${castingId}/submissions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ application_id: applicationId, submission_post_id: submissionPostId }) });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
    stageGetPlayback: async (castingId: string): Promise<{ status?: string; tracks: PlaybackTrack[] }> => {
        try {
            const r = await afetch(`${API_BASE}/api/stage/castings/${castingId}/playback`);
            const j = await r.json();
            return j && j.success ? { status: j.data?.status, tracks: j.data?.tracks || [] } : { tracks: [] };
        } catch { return { tracks: [] }; }
    },
    stagePublish: async (castingId: string, publishedPostId: string): Promise<{ success: boolean; error?: string }> => {
        try {
            const r = await afetch(`${API_BASE}/api/stage/castings/${castingId}/publish`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ published_post_id: publishedPostId }) });
            const j = await r.json().catch(() => ({}));
            return r.ok && j.success ? { success: true } : { success: false, error: j.message };
        } catch (e: any) { return { success: false, error: String(e) }; }
    },
};

// マーケット関連の型
export interface MarketAsset {
    id: string;
    sellerId: string;
    sellerName: string | null;
    sellerIcon: string | null;
    kind: string;
    title: string;
    description: string | null;
    coverUrl: string | null;
    assetUrl: string | null;
    license: string;
    priceAdp: number;
    isFree: boolean;
    status: string;
    originAssetId: string | null;
    ratingAvg: number;
    ratingCount: number;
    acquireCount: number;
    createdAt: number;
}

export interface MarketListing {
    id: string;
    kind: string;
    title: string;
    coverUrl: string | null;
    status: string;
    priceAdp: number;
    acquireCount: number;
    createdAt: number;
}

// ── 三役の街の型（バックエンド SALON/TOKIWA/STAGE_SPEC §6 と対応） ──
export interface SalonNote {
    id: string;
    author_id: string;
    title: string | null;
    body: string;
    draft_text: string | null;
    linked_post_id: string | null;
    status: string;           // 'draft' | 'public' | 'deleted'
    published_post_id: string | null;
    version: number;
    created_at: number;
    updated_at: number;
}
export interface SalonShelfItem {
    id: string;
    owner_id: string;
    title: string;
    author_name: string | null;
    note: string | null;
    category: string | null;
    spine_design: string | null;
    sort_order: number;
}
export interface SalonAnnotation {
    id: string;
    note_id: string;
    author_id: string;        // 署名制（決定2）: 必ず投稿者IDが入る
    parent_id: string | null;
    body: string;
    range_start: number | null;
    range_end: number | null;
    status: string;
    created_at: number;
}
export interface TokiwaPrompt {
    id: string;
    title: string;
    description: string | null;
    reference_asset_key: string | null;
    active_from: number;
    active_to: number;
    created_at: number;
}

/** 運用イベント1件（workers/anokoe/src/lib/opsEvents.ts が記録する。detail は512文字で切り詰め済み） */
export interface OpsEvent {
    id: string;
    at: number;          // 発生時刻（Unix秒）
    kind: string;        // OPS_EVENT の定数
    ref: string | null;  // 関連ID（work_id / post_id / room_id など）
    detail: string | null;
}

/** 直近 days 日の kind 別件数。items が空 = その期間に記録が無かったという事実 */
export interface OpsEventSummary {
    since: number;
    days: number;
    items: { kind: string; count: number }[];
}

/** 週替わりお題の対象街区（Admin手動運用・決定19） */
export type TownPromptSpace = 'tokiwa' | 'salon';

/** サロン/トキワ共通のお題（salon_prompts / tokiwa_prompts の共通列） */
export interface TownPrompt {
    id: string;
    title: string;
    description: string | null;
    active_from: number;
    active_to: number;
    created_by?: string | null;
    created_at: number;
}

// ── 共有キャンバス（RT-05）の型 ──
export interface CanvasRoom {
    id: string;
    title: string;
    host_id: string;
    status: string;                    // 'open' | 'closed'
    /**
     * 在室者数。**null は「確認中」**（DOが応答していない＝0でも実数でもない）。
     * 0 は「いまは誰もいません」という事実。数値を捏造しないための3値（決定31・事実性①）。
     */
    participant_count: number | null;
    stroke_count: number;
    image_url: string | null;
    created_at: number;
    closed_at: number | null;
}

/** 稽古場（RT-01 / B-1）。**B-1 は稽古のみで録音しない**（`recording` は常に false） */
export interface StageRoom {
    id: string;
    title: string;
    host_id: string;
    status: string;                    // 'open' | 'closed'
    kind: string;                      // 'rehearsal'（B-3 で 'live' が増える）
    casting_id: string | null;
    /**
     * 在室者数。**null は「確認中」**（DOが応答していない＝0でも実数でもない）。
     * 0 は「いまは誰もいません」という事実（決定31・事実性①）。
     * 内訳（演者/見学）にも**同じ3値の規則**が当たる。
     */
    participant_count: number | null;
    performer_count: number | null;
    listener_count: number | null;
    /** 演者定員（1〜4・ホストが開室時に指定）。3状態表示（募集中/稽古中/終幕）の分母 */
    performer_capacity: number;
    created_at: number;
    closed_at: number | null;
    /** サーバーが返す事実。B-2 でも常に false（B-3 で録音付きの回が別に増える） */
    recording: boolean;
}

/** 稽古場の在室者。`trackName` があれば「いま声が出ている」 */
export interface StageParticipant {
    userId: string;
    displayName: string;
    iconUrl: string | null;
    /** サーバーが確定した役割。見学者は声を持たない */
    role?: 'performer' | 'listener';
    trackName?: string | null;
}

/** キャンバスのストローク（座標は 0.0〜1.0 に正規化・キャンバスサイズ非依存） */
export interface CanvasStroke {
    seq: number;
    id: string;
    authorId: string;
    tool: string;                      // 'pen' | 'eraser'
    color: string;
    width: number;
    points: [number, number][];
}

export interface CanvasParticipant {
    userId: string;
    displayName: string;
    iconUrl: string | null;
    joinedAt?: number;
}

// ── 掲示板（BBS）の型 ──
/** 掲示板の設置場所（決定22: 共通1つ＋各街区1つ） */
export type BoardSpace = 'common' | 'salon' | 'tokiwa' | 'stage';

/** 引用添付（BBS-03） */
export interface BoardAttach {
    type: 'post' | 'note' | 'script';
    id: string;
}

/**
 * 掲示板のスレッド（公開表示用）。
 * ★ author_id は API が返さない（擬似匿名・BBS_SPEC §3）。表示は handle のみ。
 */
export interface BoardThread {
    id: string;
    space: BoardSpace;
    handle: string;
    title: string;
    body: string;
    attach_ref: BoardAttach | null;
    reply_count: number;
    last_reply_at: number | null;
    created_at: number;
    reaction_count: number;
}

/** 掲示板の返信（公開表示用・author_id は含まれない） */
export interface BoardReply {
    id: string;
    thread_id: string;
    handle: string;
    body: string;
    attach_ref: BoardAttach | null;
    created_at: number;
    reaction_count: number;
}

/** STG-03 一座の進行状態（都度集計の実数・決定21） */
export interface CastingProgress {
    total_capacity: number;
    filled: number;
    applied: number;
    status: string;
}
export interface TokiwaSketch {
    id: string;
    author_id: string;
    mode: 'dessin' | 'prompt' | string;
    prompt_id: string | null;
    image_url: string | null;
    image_key: string | null;
    elapsed_sec: number | null;
    published_post_id: string | null;
    status: string;
    created_at: number;
}
export interface StageSlot {
    role_id: string;
    role_name?: string;
    kind?: 'cast' | 'crew' | string; // crew = 裏方（先行募集ラベル対象・決定12）
    capacity?: number;
    preview?: boolean;               // 裏方の「先行募集」フラグ（決定12）
}
export interface StageScript {
    id: string;
    author_id: string;
    title: string;
    body: string;
    role_slots: string | null;       // JSON文字列
    origin_ref: string | null;       // JSON文字列（サロン参照インポート元・読み取り専用/決定11）
    credited_author: string | null;
    published_post_id: string | null;
    version: number;
    status: string;
    created_at: number;
}
export interface StageCasting {
    id: string;
    script_id: string;
    host_id: string;
    title: string | null;
    slots: string | StageSlot[];     // 詳細取得時はparse済み配列
    status: string;                  // open/casting/ready/performed/closed
    published_post_id: string | null;
    created_at: number;
}
export interface StageApplication {
    id: string;
    casting_id: string;
    role_id: string;
    applicant_id: string;
    status: string;                  // applied/accepted/declined/withdrawn
    submission_post_id: string | null;
    created_at: number;
}
export interface PlaybackTrack {
    role_id: string;
    role_name: string | null;
    order: number;
    applicant_id: string | null;
    submission_post_id: string | null;
    url: string | null;
    empty: boolean;                  // 未提出役＝空席（無音で飛ばす・決定9）
}
